import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { LogIn, Mail, ArrowLeft, Languages } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { useApp } from "../../contexts/AppContext";
import { useSendOTPMutation, useVerifyOTPMutation } from "../../store/api/auth";
import { setUser } from "../../store/slices/authSlice";
import storage from "../../utils/storage";
import { STORAGE_KEYS } from "../../utils/constants";
import { handleApiError } from "../../utils/apiErrors";

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, language, setLanguage } = useLanguage();
  const { demoLogin, setAuthUser } = useApp();
  
  const [sendOTP, { isLoading: isSendingOTP }] = useSendOTPMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = useVerifyOTPMutation();
  
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  
  const otpInputRef = useRef<HTMLInputElement>(null);
  
  // Focus OTP input when step changes to 'otp'
  useEffect(() => {
    if (step === 'otp' && otpInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        otpInputRef.current?.focus();
        otpInputRef.current?.select();
      }, 100);
    }
  }, [step]);

  const toggleLanguage = () => {
    setLanguage(language === 'ja' ? 'en' : 'ja');
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('auth.emailRequired'));
      return;
    }

    try {
      const resp: any = await sendOTP({ email });
      
      if (resp.data) {
        setOtpSent(true);
        setStep('otp');
        toast.success(resp.data.message || t('auth.otpSent'));
      }
      
      if (resp.error) {
        handleApiError(resp.error, t('auth.otpSendFailed'));
      }
    } catch (error: any) {
      console.error('Send OTP error:', error);
      handleApiError(error, t('auth.otpSendFailed'));
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp) {
      toast.error(t('auth.enterOtpRequired'));
      return;
    }

    if (otp.length !== 6) {
      toast.error(t('auth.otpLength'));
      return;
    }

    try {
      const resp: any = await verifyOTP({ email, otp });
      
      if (resp.data) {
        // Store tokens
        storage.set(STORAGE_KEYS.ACCESS_TOKEN, resp.data.access_token);
        if (resp.data.refresh_token) {
          storage.set(STORAGE_KEYS.REFRESH_TOKEN, resp.data.refresh_token);
        }
        
        // Store user info
        storage.set(STORAGE_KEYS.USER_INFO, resp.data.user);
        
        // Store current workspace from user data if available
        if (resp.data.user?.currentWorkspaceId) {
          storage.set(STORAGE_KEYS.CURRENT_WORKSPACE, resp.data.user.currentWorkspaceId);
        }
        
        // Store role from user data if available
        if (resp.data.user?.role) {
          storage.set(STORAGE_KEYS.CURRENT_ROLE, resp.data.user.role);
        }
        
        // Update Redux state
        dispatch(setUser(resp.data.user));
        
        // Update AppContext state
        setAuthUser(resp.data.user);
        
        toast.success(t('auth.loginSuccess'));
        
        // Navigate based on role
        if (resp.data.user?.role === 'guest') {
          navigate('/tasks');
        } else {
          navigate('/');
        }
      }
      
      if (resp.error) {
        handleApiError(resp.error, t('auth.otpInvalidOrExpired'));
      }
    } catch (error: any) {
      console.error('Verify OTP error:', error);
      handleApiError(error, t('auth.otpInvalidOrExpired'));
    }
  };

  const handleDemoLogin = (demoEmail: string) => {
    setIsLoading(true);
    
    setTimeout(() => {
      const success = demoLogin(demoEmail);
      
      if (success) {
        toast.success(t('auth.demoLoginSuccess'));
        navigate('/');
      } else {
        toast.error(t('auth.demoLoginFailed'));
      }
      setIsLoading(false);
    }, 300);
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtp("");
    setOtpSent(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* 言語切り替えボタン */}
      <div className="absolute top-4 right-4">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLanguage}
          className="gap-2"
        >
          <Languages className="w-4 h-4" />
          {language === 'ja' ? t('auth.switchToEnglish') : t('auth.switchToJapanese')}
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <LogIn className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t('auth.loginTitle')}</CardTitle>
          <CardDescription>
            {step === 'email' 
              ? t('auth.emailInputDesc')
              : t('auth.otpInputDesc')
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div className="space-y-2">
                <Label className="mb-2" htmlFor="email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isSendingOTP}>
                {isSendingOTP 
                  ? t('auth.sending')
                  : t('auth.sendOtpButton')
                }
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="mb-2" htmlFor="otp">
                    {t('auth.otpLabel')}
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToEmail}
                    className="h-auto p-0 text-xs"
                  >
                    <ArrowLeft className="w-3 h-3 mr-1" />
                    {t('common.back')}
                  </Button>
                </div>
                <Input
                  ref={otpInputRef}
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  disabled={isLoading}
                  maxLength={6}
                  autoComplete="one-time-code"
                />
                <p className="text-xs text-gray-500 text-center">
                  {email}
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={isVerifyingOTP || otp.length !== 6}>
                {isVerifyingOTP ? t('auth.loggingIn') : t('auth.login')}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSendOTP}
                className="w-full"
                disabled={isSendingOTP}
              >
                {t('auth.resendOtp')}
              </Button>
            </form>
          )}

          {step === 'email' && (
            <>
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">{t('auth.or')}</span>
                  </div>
                </div>

                {/* <div className="mt-6 space-y-3">
                  <p className="text-sm text-gray-700 mb-3">{t('auth.demoAccountsTitle')}</p>
                  
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleDemoLogin("admin@example.com")}
                    disabled={isLoading}
                  >
                    <User className="w-4 h-4 mr-2" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{t('auth.adminAccount')}</div>
                      <div className="text-xs text-gray-500">{t('auth.adminAccountDesc')}</div>
                    </div>
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleDemoLogin("guest@example.com")}
                    disabled={isLoading}
                  >
                    <UserCheck className="w-4 h-4 mr-2" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{t('auth.guestAccount')}</div>
                      <div className="text-xs text-gray-500">{t('auth.guestAccountDesc')}</div>
                    </div>
                  </Button>
                </div> */}
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  {t('auth.noWorkspace')}
                </p>
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate("/signup")}
                  disabled={isLoading}
                  className="mt-2"
                >
                  {t('auth.createWorkspace')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

